/**
 * MessageBubble Component
 * 
 * Displays a single message in iMessage style:
 * - Own messages: right side, blue bubble
 * - Other messages: left side, gray bubble
 * - Bubble width based on content (max 65%)
 * - Timestamps hidden by default, show on hover
 * - Grouped messages have tighter spacing
 * - Long-press/right-click shows action menu (reactions + delete)
 * - Click on reaction to toggle
 * - Image grid for multiple images
 * - Full-size image viewer
 * - Voice memo player with speed controls
 * - Delete message functionality
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Check, CheckCheck, FileText, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatMessageTime } from './utils/timeFormat';
import { MESSAGE_TYPES, toggleReaction, deleteMessageForEveryone, editMessage, DELETE_FOR_EVERYONE_WINDOW, voteOnDmPoll, voteOnDmPollOther } from '@/lib/firestore';
import { PollView } from '@/components/comms/components/PostMedia';
import { stripHtml } from './utils/htmlUtils';
import { sanitizeHtml } from '../../utils/sanitize';
import MessageActionMenu from './MessageActionMenu';
import DeleteMessageDialog from './DeleteMessageDialog';
import ImageGrid from './ImageGrid';
import ImageViewer from './ImageViewer';
import VoiceMemoPlayer from './VoiceMemoPlayer';
import VideoPlayer from '@/components/shared/VideoPlayer';
import AudioPlayer from '@/components/shared/AudioPlayer';
import AttachmentViewer from '@/components/shared/AttachmentViewer';

// Extract file attachments from message HTML (data-file-attachment paragraphs)
function extractFileAttachments(html) {
  if (!html || !html.includes('data-file-attachment')) return { cleanHtml: html, files: [] };
  const files = [];
  const cleanHtml = html.replace(/<p[^>]*data-file-attachment="true"[^>]*data-file-url="([^"]*)"[^>]*data-file-name="([^"]*)"[^>]*data-file-size="([^"]*)"[^>]*data-file-type="([^"]*)"[^>]*>.*?<\/p>/g,
    (match, url, filename, size, type) => {
      // Only http(s) links become chips (a javascript:/data: URL would reach an <iframe src>)
      if (/^https?:\/\//i.test(url || '')) files.push({ url, filename, size: parseInt(size, 10), type });
      return '';
    }
  );
  return { cleanHtml: cleanHtml.trim(), files };
}

// Extract first video embed URL from message text (YouTube, Loom, Vimeo)
function extractVideoEmbed(text) {
  if (!text) return null;
  const yt = text.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return { type: 'YouTube', embedUrl: `https://www.youtube-nocookie.com/embed/${yt[1]}`, watchUrl: `https://www.youtube.com/watch?v=${yt[1]}` };
  const loom = text.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loom) return { type: 'Loom', embedUrl: `https://www.loom.com/embed/${loom[1]}`, watchUrl: `https://www.loom.com/share/${loom[1]}` };
  const vimeo = text.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { type: 'Vimeo', embedUrl: `https://player.vimeo.com/video/${vimeo[1]}`, watchUrl: `https://vimeo.com/${vimeo[1]}` };
  return null;
}

// Long press duration in ms
const LONG_PRESS_DURATION = 500;

/**
 * Highlight matching text in a string
 */
function HighlightedText({ text, highlight, isOwnMessage }) {
  if (!highlight || !text) return <span>{text}</span>;
  
  const lowerText = text.toLowerCase();
  const lowerHighlight = highlight.toLowerCase();
  const parts = [];
  let lastIndex = 0;
  let index = lowerText.indexOf(lowerHighlight);
  
  while (index !== -1) {
    // Add text before match
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    // Add highlighted match
    parts.push(
      <mark key={index} data-ui="hit" className={`${isOwnMessage ? 'bg-yellow-300 text-blue-900' : 'bg-yellow-200 text-yellow-900'} rounded px-0.5`}>
        {text.slice(index, index + highlight.length)}
      </mark>
    );
    lastIndex = index + highlight.length;
    index = lowerText.indexOf(lowerHighlight, lastIndex);
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return <>{parts}</>;
}

export default function MessageBubble({
  message,
  isOwnMessage,
  isFirstInGroup,
  isLastInGroup,
  showSenderName,
  conversationParticipants = [],
  participantDetails = {},
  isGroupChat = false,
  conversationId,
  currentUserId,
  currentUser,
  onReactionUpdate,
  highlightText = '',
  removedParticipants = {},
  onRetryMessage,
  onCancelMessage,
  onReply,
  showHighlightRing = false,
}) {
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [menuAnchorPoint, setMenuAnchorPoint] = useState(null); // pointer position for right-click; null = anchor to the bubble
  const [isReacting, setIsReacting] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [attachmentToView, setAttachmentToView] = useState(null);
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const editInputRef = useRef(null);
  
  // Long press handling
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);
  
  // Track if last interaction was touch (to prevent mouseLeave from hiding timestamp on mobile)
  const isTouchInteraction = useRef(false);
  
  // Timer for auto-hiding timestamp on mobile
  const timestampHideTimer = useRef(null);

  // Ref for anchoring the action menu portal
  const bubbleContainerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timestampHideTimer.current) clearTimeout(timestampHideTimer.current);
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  // Handle opening image viewer
  const handleImageClick = useCallback((index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  }, []);

  // Determine bubble styling based on position in group
  const getBubbleRadius = () => {
    if (isOwnMessage) {
      // Own messages - right side
      if (isFirstInGroup && isLastInGroup) return 'rounded-2xl rounded-br-md';
      if (isFirstInGroup) return 'rounded-2xl rounded-br-md rounded-br-md';
      if (isLastInGroup) return 'rounded-2xl rounded-tr-md rounded-br-md';
      return 'rounded-2xl rounded-r-md';
    } else {
      // Other messages - left side
      if (isFirstInGroup && isLastInGroup) return 'rounded-2xl rounded-bl-md';
      if (isFirstInGroup) return 'rounded-2xl rounded-bl-md';
      if (isLastInGroup) return 'rounded-2xl rounded-tl-md rounded-bl-md';
      return 'rounded-2xl rounded-l-md';
    }
  };

  // Get spacing based on grouping
  const getSpacing = () => {
    if (isFirstInGroup) return 'mt-3';
    return 'mt-0.5';
  };

  // Get current user's reactions on this message
  const getCurrentUserReactions = () => {
    if (!message.reactions || !currentUserId) return [];
    return Object.entries(message.reactions)
      .filter(([_, users]) => users.includes(currentUserId))
      .map(([emoji]) => emoji);
  };

  // Toggle timestamp visibility (for tap on mobile)
  const handleTimestampToggle = useCallback(() => {
    // Clear any existing auto-hide timer
    if (timestampHideTimer.current) {
      clearTimeout(timestampHideTimer.current);
      timestampHideTimer.current = null;
    }
    
    // Use functional update to ensure we have the latest state
    setShowTimestamp(prev => {
      const newValue = !prev;
      // If showing on touch device, set auto-hide timer
      if (newValue && isTouchInteraction.current) {
        timestampHideTimer.current = setTimeout(() => {
          setShowTimestamp(false);
        }, 3000);
      }
      return newValue;
    });
  }, []);

  // Poll message helpers (DM polls reuse the feed's <PollView> module).
  // Voter names come from the conversation's participant details (same source
  // the read-receipt "Seen by" popover uses) — no server-side resolution needed.
  const pollVoterNames = useCallback(() => {
    const names = {};
    Object.entries(participantDetails || {}).forEach(([id, d]) => {
      names[id] = d?.name || d?.email || 'User';
    });
    if (currentUserId && !names[currentUserId]) {
      names[currentUserId] = currentUser?.name || currentUser?.email || 'You';
    }
    return names;
  }, [participantDetails, currentUserId, currentUser]);

  const handlePollVote = useCallback(async (optionIndex) => {
    if (!conversationId || !currentUserId) return;
    // Return the committed poll so <PollView> reconciles immediately.
    return voteOnDmPoll(conversationId, message.id, currentUserId, optionIndex);
  }, [conversationId, message.id, currentUserId]);

  const handlePollVoteOther = useCallback(async (text) => {
    if (!conversationId || !currentUserId) return;
    return voteOnDmPollOther(conversationId, message.id, currentUserId, text);
  }, [conversationId, message.id, currentUserId]);


  // Handle long press start — disabled for pending messages
  const handleTouchStart = useCallback(() => {
    if (message._pending) return;
    isTouchInteraction.current = true;
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      // Clear any native word-selection the browser started during the press
      // (selectable text) so it can't fight the reaction menu.
      try { window.getSelection()?.removeAllRanges(); } catch (_) { /* ignore */ }
      setMenuAnchorPoint(null);
      setShowActionMenu(true);
    }, LONG_PRESS_DURATION);
  }, [message._pending]);

  // Handle long press end
  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  // Is there a real (non-collapsed, non-empty) text selection inside THIS bubble?
  const selectionInBubble = useCallback(() => {
    try {
      const sel = window.getSelection();
      return !!(
        sel && !sel.isCollapsed && sel.rangeCount > 0 && sel.toString().trim() &&
        sel.anchorNode && bubbleContainerRef.current?.contains(sel.anchorNode)
      );
    } catch (_) { return false; }
  }, []);

  // Did the pointer land inside the live selection? (Chromium/WebKit expose
  // caretRangeFromPoint, Firefox caretPositionFromPoint.)
  const pointInSelection = (clientX, clientY) => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return false;
      const range = sel.getRangeAt(0);
      let node = null; let offset = 0;
      if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(clientX, clientY);
        if (pos) { node = pos.offsetNode; offset = pos.offset; }
      } else if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(clientX, clientY);
        if (r) { node = r.startContainer; offset = r.startOffset; }
      }
      return !!node && range.isPointInRange(node, offset);
    } catch (_) { return false; }
  };

  // Handle right-click / ctrl-click (context menu) — disabled for pending messages
  const handleContextMenu = useCallback((e) => {
    if (message._pending) return;
    // Inside the inline edit box or an embedded player the native menu is the
    // right one (cut/paste, player controls).
    if (e.target?.closest?.('textarea, input, [contenteditable="true"], iframe, video, audio')) return;
    if (selectionInBubble()) {
      // Defer to the NATIVE menu only when the right-click landed INSIDE the
      // live selection (the user selected text and wants copy / look-up). A
      // selection left behind by an earlier gesture — the double-click ❤️
      // selects the word under the cursor, a drag-to-copy persists until the
      // user clicks into other text — must never block the reaction menu.
      // That stale selection was the "right-click sometimes does nothing" bug.
      if (pointInSelection(e.clientX, e.clientY)) return;
      try { window.getSelection()?.removeAllRanges(); } catch (_) { /* ignore */ }
    }
    e.preventDefault();
    setMenuAnchorPoint({ x: e.clientX, y: e.clientY });
    setShowActionMenu(true);
  }, [message._pending, selectionInBubble]);

  // Build notification options for reactions
  const getReactionNotificationOptions = useCallback(() => ({
    reactorName: currentUser?.name || currentUser?.email || 'Someone',
    reactorAvatar: currentUser?.avatar,
    churchId: currentUser?.church_id
  }), [currentUser]);

  // Handle double-click to quick react with ❤️
  const handleDoubleClick = useCallback(async () => {
    // The browser selects the word under the cursor on double-click; that
    // selection is an artefact of the quick-react gesture, never intended, and
    // would otherwise block the next right-click on this bubble.
    try { window.getSelection()?.removeAllRanges(); } catch (_) { /* ignore */ }
    if (!conversationId || !currentUserId || isReacting) return;
    
    setIsReacting(true);
    try {
      await toggleReaction(conversationId, message.id, '❤️', currentUserId, getReactionNotificationOptions());
      if (onReactionUpdate) onReactionUpdate();
    } catch (err) {
      console.error('Error adding reaction:', err);
    } finally {
      setIsReacting(false);
    }
  }, [conversationId, message.id, currentUserId, isReacting, onReactionUpdate, getReactionNotificationOptions]);

  // Handle selecting an emoji from the action menu
  const handleEmojiSelect = useCallback(async (emoji) => {
    if (!conversationId || !currentUserId || isReacting) return;
    
    setIsReacting(true);
    try {
      await toggleReaction(conversationId, message.id, emoji, currentUserId, getReactionNotificationOptions());
      if (onReactionUpdate) onReactionUpdate();
    } catch (err) {
      console.error('Error toggling reaction:', err);
    } finally {
      setIsReacting(false);
    }
  }, [conversationId, message.id, currentUserId, isReacting, onReactionUpdate, getReactionNotificationOptions]);

  // Handle clicking on an existing reaction to toggle it
  const handleReactionClick = useCallback(async (emoji) => {
    if (!conversationId || !currentUserId || isReacting) return;
    
    setIsReacting(true);
    try {
      await toggleReaction(conversationId, message.id, emoji, currentUserId, getReactionNotificationOptions());
      if (onReactionUpdate) onReactionUpdate();
    } catch (err) {
      console.error('Error toggling reaction:', err);
    } finally {
      setIsReacting(false);
    }
  }, [conversationId, message.id, currentUserId, isReacting, onReactionUpdate, getReactionNotificationOptions]);

  // Handle touch on existing reaction - more reliable on mobile
  const handleReactionTouch = useCallback((emoji, e) => {
    e.stopPropagation();
    e.preventDefault();
    handleReactionClick(emoji);
  }, [handleReactionClick]);

  // Handle delete button click - opens confirmation dialog
  const handleDeleteClick = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  // Handle confirming delete
  const handleConfirmDelete = useCallback(async () => {
    if (!conversationId || !message.id || !currentUserId || isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteMessageForEveryone(conversationId, message.id, currentUserId);
      setShowDeleteDialog(false);
    } catch (err) {
      console.error('Error deleting message:', err);
      // Show error to user
      toast.error('Couldn\'t delete this message. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [conversationId, message.id, currentUserId, isDeleting]);

  // Check if message is editable (own text message, within 1 hour, not deleted)
  const canEdit = isOwnMessage
    && message.text
    && !message.deletedForEveryone
    && !message.images?.length
    && !message.voiceMemo
    && !message.video
    && (() => {
      const createdAt = message.createdAt?.toDate?.() || (message.createdAt ? new Date(message.createdAt) : null);
      if (!createdAt) return false;
      return (Date.now() - createdAt.getTime()) <= DELETE_FOR_EVERYONE_WINDOW;
    })();

  // Handle edit button click
  const handleEditClick = useCallback(() => {
    const plain = stripHtml(message.text || '');
    setEditText(plain);
    setIsEditing(true);
    setTimeout(() => editInputRef.current?.focus(), 50);
  }, [message.text]);

  // Handle saving edit
  const handleSaveEdit = useCallback(async () => {
    if (!conversationId || !message.id || !currentUserId || isSavingEdit) return;
    const trimmed = editText.trim();
    if (!trimmed || trimmed === stripHtml(message.text || '').trim()) {
      setIsEditing(false);
      return;
    }
    setIsSavingEdit(true);
    try {
      await editMessage(conversationId, message.id, currentUserId, trimmed);
      setIsEditing(false);
    } catch (err) {
      console.error('Error editing message:', err);
      toast.error(err.message || 'Couldn\'t edit this message.');
    } finally {
      setIsSavingEdit(false);
    }
  }, [conversationId, message.id, currentUserId, editText, isSavingEdit, message.text]);

  // Handle system messages (user joined, left, etc.)
  if (message.type === MESSAGE_TYPES.SYSTEM) {
    return (
      <div className="flex justify-center my-3">
        <span data-ui="sys-msg" className="text-xs text-slate-400 dark:text-slate-500 italic bg-slate-50 dark:bg-[#1e1f22] px-3 py-1 rounded-full">
          {message.text}
        </span>
      </div>
    );
  }

  // Handle deleted messages
  if (message.deletedForEveryone) {
    return (
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${getSpacing()}`}>
        <div data-ui="deleted-msg" className="max-w-[65%] px-4 py-2 rounded-2xl bg-slate-100 dark:bg-[#3a3d41] text-slate-400 dark:text-slate-500 italic text-sm">
          This message was deleted
        </div>
      </div>
    );
  }

  // Get read status display
  const getStatusIcon = () => {
    if (!isOwnMessage) return null;
    
    const readBy = message.readBy || [];
    const otherParticipants = conversationParticipants.filter(id => id !== message.senderId);
    const readByOthers = otherParticipants.filter(id => readBy.includes(id));

    // Sending state
    if (message.status === 'sending') {
      return <span data-ui="msg-st" data-state="sending" className="text-[10px] text-slate-400 dark:text-slate-500 italic">Sending...</span>;
    }

    // Failed state
    if (message.status === 'failed') {
      return (
        <span data-ui="msg-st" data-state="failed" className="flex items-center gap-1.5">
          <span className="text-[10px] text-red-400">Failed to send</span>
          {onRetryMessage && (
            <>
              <span className="text-[10px] text-slate-300">·</span>
              <button 
                onClick={(e) => { e.stopPropagation(); onRetryMessage(message._tempId); }}
                className="text-[10px] text-blue-500 dark:text-blue-400 hover:text-blue-600"
              >
                Retry
              </button>
            </>
          )}
          {onCancelMessage && (
            <button 
              onClick={(e) => { e.stopPropagation(); onCancelMessage(message._tempId); }}
              className="text-[10px] text-slate-400 dark:text-slate-500 hover:text-slate-500 ml-0.5"
            >
              ✕
            </button>
          )}
        </span>
      );
    }

    // No one has read yet - Delivered
    if (readByOthers.length === 0) {
      return (
        <span data-ui="msg-st" data-state="delivered" className="flex items-center gap-1 text-slate-400 dark:text-slate-500">
          <Check className="w-3.5 h-3.5" />
          <span className="text-[10px]">Delivered</span>
        </span>
      );
    }

    // Everyone has read - Read
    if (readByOthers.length === otherParticipants.length) {
      return (
        <span data-ui="msg-st" data-state="read" className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
          <CheckCheck className="w-3.5 h-3.5" />
          <span className="text-[10px]">Read</span>
        </span>
      );
    }

    // Partial read in group chat
    if (isGroupChat && readByOthers.length > 0) {
      return (
        <span data-ui="msg-st" data-state="read" className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
          <CheckCheck className="w-3.5 h-3.5" />
          <span className="text-[10px]">Read by {readByOthers.length}</span>
        </span>
      );
    }

    // Fallback - Delivered
    return (
      <span data-ui="msg-st" data-state="delivered" className="flex items-center gap-1 text-slate-400 dark:text-slate-500">
        <Check className="w-3.5 h-3.5" />
        <span className="text-[10px]">Delivered</span>
      </span>
    );
  };

  const currentUserReactions = getCurrentUserReactions();

  return (
    <div 
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${getSpacing()} ${
        message.status === 'sending' ? 'opacity-80' : ''
      }`}
      onMouseMove={() => {
        // Real mouse movement resets the touch flag (for hybrid devices)
        isTouchInteraction.current = false;
      }}
      onMouseEnter={() => {
        // Only show on hover for desktop (non-touch)
        if (!isTouchInteraction.current) {
          setShowTimestamp(true);
        }
      }}
      onMouseLeave={() => {
        // Only hide on mouse leave for desktop (non-touch)
        // On mobile, tapping elsewhere or scrolling will eventually hide it
        if (!isTouchInteraction.current) {
          setShowTimestamp(false);
        }
      }}
    >
      {/* Timestamp on LEFT side for own messages (right-aligned bubbles) */}
      {isOwnMessage && (
        <div className={`flex items-center mr-2 transition-opacity duration-200 ${showTimestamp ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <span data-ui="msg-ts" className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
            {message.edited && <span className="italic mr-1">edited</span>}
            {formatMessageTime(message.createdAt)}
          </span>
        </div>
      )}
      
      <div className="max-w-[65%] flex flex-col relative" ref={bubbleContainerRef}>
        {/* Sender name for group chats */}
        {showSenderName && isGroupChat && (
          <span data-ui="who" className={`text-xs ml-3 mb-1 ${removedParticipants[message.senderId] ? 'text-slate-400 dark:text-slate-500 italic' : 'text-slate-500 dark:text-slate-400'}`}>
            {removedParticipants[message.senderId] 
              ? `${message.senderName?.split(' ')[0] || 'User'} (no longer a user)`
              : (message.senderName?.split(' ')[0] || 'Unknown')
            }
          </span>
        )}

        {/* Determine if this is an image-only or voice-only message (no text) */}
        {(() => {
          const { cleanHtml: textWithoutFiles, files: fileAttachments } = extractFileAttachments(message.text);
          const videoEmbed = extractVideoEmbed(message.text);
          const isImageOnlyMessage = message.type === MESSAGE_TYPES.IMAGE && message.images?.length > 0 && !message.text;
          const isVoiceOnlyMessage = message.type === MESSAGE_TYPES.VOICE && message.voiceMemo && !message.text;
          const isVideoOnlyMessage = message.type === MESSAGE_TYPES.VIDEO && message.video && !message.text;
          const isPollMessage = message.type === MESSAGE_TYPES.POLL && message.poll;

          // Poll message — render the SHARED <PollView> (same module as the feed),
          // backed by Firestore votes. Self-contained card, no chat bubble.
          if (isPollMessage) {
            return (
              <div
                data-ui="poll-card"
                className="relative w-[320px] max-w-full rounded-2xl bg-white dark:bg-[#2a2d31] border border-slate-200 dark:border-white/10 p-3 select-none"
                onContextMenu={handleContextMenu}
                data-testid="poll-message-bubble"
              >
                {showHighlightRing && <div className="notif-highlight-ring rounded-2xl" aria-hidden="true" />}
                <PollView
                  poll={message.poll}
                  currentUserId={currentUserId}
                  voterNames={pollVoterNames()}
                  onVote={message._pending ? undefined : handlePollVote}
                  onVoteOther={message._pending ? undefined : handlePollVoteOther}
                  readOnly={!!message._pending}
                />
              </div>
            );
          }

          // Video-only message - special styling without bubble
          if (isVideoOnlyMessage) {
            return (
              <div
                className="relative cursor-pointer select-none max-w-[320px]"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                onContextMenu={handleContextMenu}
                onDoubleClick={handleDoubleClick}
                onClick={() => { if (!isLongPress.current) handleTimestampToggle(); }}
                data-testid="video-message-bubble"
              >
                {showHighlightRing && <div className="notif-highlight-ring rounded-2xl" aria-hidden="true" />}
                <VideoPlayer
                  src={message.video.url}
                  thumbnailUrl={message.video.thumbnailUrl}
                  duration={message.video.duration}
                />
              </div>
            );
          }

          // Voice-only message - special styling without blue bubble
          if (isVoiceOnlyMessage) {
            return (
              <div
                className="relative cursor-pointer select-none"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                onContextMenu={handleContextMenu}
                onDoubleClick={handleDoubleClick}
                onClick={() => {
                  if (!isLongPress.current) {
                    handleTimestampToggle();
                  }
                }}
                data-testid="voice-message-bubble"
              >
                {showHighlightRing && <div className="notif-highlight-ring rounded-2xl" aria-hidden="true" />}
                <VoiceMemoPlayer 
                  voiceMemo={message.voiceMemo}
                  isOwnMessage={isOwnMessage}
                />
              </div>
            );
          }
          
          return isImageOnlyMessage ? (
            // Image-only message - no bubble background
            <div
              className="relative cursor-pointer select-none"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              onContextMenu={handleContextMenu}
              onDoubleClick={handleDoubleClick}
              onClick={() => {
                if (!isLongPress.current) {
                  handleTimestampToggle();
                }
              }}
            >
              {showHighlightRing && <div className="notif-highlight-ring rounded-2xl" aria-hidden="true" />}
              <ImageGrid 
                images={message.images} 
                onImageClick={handleImageClick}
              />
            </div>
          ) : (
            // Regular message with bubble background
            <div
              data-ui={isOwnMessage ? 'bubble-me' : 'bubble-them'}
              data-pos={isFirstInGroup ? (isLastInGroup ? 'single' : 'first') : (isLastInGroup ? 'last' : 'mid')}
              className={`
                relative px-4 py-2 break-words select-text dm-bubble-selectable
                ${getBubbleRadius()}
                ${isEditing ? 'w-[320px] max-w-full' : ''}
                ${isOwnMessage 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-slate-200 dark:bg-[#3e4145] text-slate-900 dark:text-slate-100'
                }
                transition-transform active:scale-[0.98]
              `}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              onContextMenu={handleContextMenu}
              onDoubleClick={handleDoubleClick}
              onClick={() => {
                if (!isLongPress.current && !selectionInBubble()) {
                  handleTimestampToggle();
                }
              }}
            >
              {showHighlightRing && <div className={`notif-highlight-ring ${getBubbleRadius()}`} aria-hidden="true" />}
              {/* Reply-to preview */}
              {message.replyTo && message.replyTo.text && (
                <div data-ui="msg-quote" className={`text-xs rounded-md px-2.5 py-1.5 mb-1.5 border-l-2 ${isOwnMessage ? 'bg-white/20 border-white/50' : 'bg-slate-100 dark:bg-[#3a3d41] border-slate-300 dark:border-white/15'}`}>
                  <div data-ui="quote-name" className={`font-semibold ${isOwnMessage ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>{message.replyTo.senderName || 'User'}</div>
                  <div className={`truncate ${isOwnMessage ? 'text-white/60' : 'text-slate-400 dark:text-slate-500'}`}>{(() => { const t = stripHtml(message.replyTo.text || ''); return t.length > 80 ? t.slice(0, 80) + '...' : t; })()}</div>
                </div>
              )}

              {/* Text content — render as HTML for formatted messages */}
              {isEditing ? (
                <div className="w-full" data-testid="edit-message-input-container">
                  <textarea
                    ref={editInputRef}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                      if (e.key === 'Escape') { setIsEditing(false); }
                    }}
                    className={`w-full text-[15px] leading-relaxed rounded-lg p-2 resize-none border ${
                      isOwnMessage
                        ? 'bg-blue-400/30 border-blue-300/50 text-white placeholder-blue-200'
                        : 'bg-card border-slate-300 dark:border-white/15 text-slate-900 dark:text-slate-100'
                    } focus:outline-none focus:ring-1 focus:ring-blue-400`}
                    rows={Math.min(editText.split('\n').length, 5)}
                    data-ui="edit-box"
                    data-testid="edit-message-textarea"
                  />
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={handleSaveEdit}
                      disabled={isSavingEdit || !editText.trim()}
                      data-ui="edit-save"
                      className="text-xs font-medium px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white disabled:opacity-40 transition-colors"
                      data-testid="edit-message-save-btn"
                    >
                      {isSavingEdit ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className={`text-xs px-3 py-1 rounded-md transition-colors ${
                        isOwnMessage ? 'text-blue-100 hover:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100'
                      }`}
                      data-testid="edit-message-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : textWithoutFiles && (() => {
                // Mobile sends plain text with \n; web sends HTML with <p>/<br> tags.
                // Convert \n to <br> for plain-text messages so line breaks render correctly.
                const hasHtml = /<[a-z][\s\S]*?>/i.test(textWithoutFiles);
                const displayText = hasHtml ? textWithoutFiles : textWithoutFiles.replace(/\n/g, '<br>');
                return highlightText ? (
                  <p className="text-[15px] leading-normal tracking-[-0.01em] whitespace-pre-wrap">
                    <HighlightedText text={stripHtml(textWithoutFiles)} highlight={highlightText} isOwnMessage={isOwnMessage} />
                  </p>
                ) : (
                  <div
                    data-ui="msg-text"
                    className={`text-[15px] leading-normal tracking-[-0.01em] message-html-content [&_p]:m-0 [&_p]:whitespace-pre-wrap [&_a]:underline ${isOwnMessage ? '[&_a]:text-white' : '[&_a]:text-blue-600'}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayText) }}
                  />
                );
              })()}

              {/* Video embed (YouTube, Loom, Vimeo) */}
              {videoEmbed && (
                <div className="mt-2">
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe
                      src={videoEmbed.embedUrl}
                      className="w-full h-full border-0"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                      title={`${videoEmbed.type} video`}
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-1 text-right">
                    <a
                      href={videoEmbed.watchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-ui="openin"
                      className={`inline-flex items-center gap-1 text-[10px] ${isOwnMessage ? 'text-white/70 hover:text-white' : 'text-slate-400 dark:text-slate-500 hover:text-blue-500'}`}
                    >
                      Open in {videoEmbed.type} ↗
                    </a>
                  </div>
                </div>
              )}

              {/* Image content with caption - using ImageGrid component */}
              {message.type === MESSAGE_TYPES.IMAGE && message.images && message.images.length > 0 && (
                <div className={message.text ? 'mt-2' : ''}>
                  <ImageGrid 
                    images={message.images} 
                    onImageClick={handleImageClick}
                  />
                </div>
              )}

              {/* Voice memo content - using VoiceMemoPlayer component */}
              {message.type === MESSAGE_TYPES.VOICE && message.voiceMemo && (
                <div className={`${message.text ? 'mt-2' : ''}`}>
                  <VoiceMemoPlayer 
                    voiceMemo={message.voiceMemo}
                    isOwnMessage={isOwnMessage}
                  />
                </div>
              )}

              {/* Video content */}
              {message.type === MESSAGE_TYPES.VIDEO && message.video && (
                <div className={`${message.text ? 'mt-2' : ''} max-w-[320px]`}>
                  <VideoPlayer
                    src={message.video.url}
                    thumbnailUrl={message.video.thumbnailUrl}
                    duration={message.video.duration}
                  />
                </div>
              )}

              {/* File attachments */}
              {fileAttachments.length > 0 && (
                <div className={`space-y-1.5 ${textWithoutFiles ? 'mt-2' : ''}`}>
                  {fileAttachments.map((file, idx) => {
                    const isAudio = file.type && file.type.startsWith('audio/');
                    if (isAudio) {
                      return (
                        <AudioPlayer
                          key={idx}
                          src={file.url}
                          filename={file.filename}
                          isOwnMessage={isOwnMessage}
                          compact
                        />
                      );
                    }
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setAttachmentToView(file)}
                        data-ui="file-chip"
                        className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors ${
                          isOwnMessage
                            ? 'bg-blue-400/20 border-blue-400/30 hover:bg-blue-400/30'
                            : 'bg-slate-100 dark:bg-[#3a3d41] border-slate-200 dark:border-white/10 hover:bg-slate-200'
                        }`}
                      >
                        <FileText className={`w-4 h-4 flex-shrink-0 ${isOwnMessage ? 'text-blue-100' : 'text-slate-400 dark:text-slate-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-medium truncate ${isOwnMessage ? 'text-white' : 'text-slate-700 dark:text-slate-200'}`}>{file.filename}</p>
                          <p className={`text-[10px] ${isOwnMessage ? 'text-blue-200' : 'text-slate-400 dark:text-slate-500'}`}>{file.size ? `${(file.size / 1024).toFixed(0)} KB` : ''}</p>
                        </div>
                        <Download className={`w-3.5 h-3.5 flex-shrink-0 ${isOwnMessage ? 'text-blue-200' : 'text-slate-400 dark:text-slate-500'}`} />
                      </button>
                    );
                  })}
                </div>
              )}

              {attachmentToView && (
                <AttachmentViewer file={attachmentToView} onClose={() => setAttachmentToView(null)} />
              )}
            </div>
          );
        })()}

        {/* Image Viewer Modal */}
        {viewerOpen && message.images && (
          <ImageViewer
            images={message.images}
            currentIndex={viewerIndex}
            onClose={() => setViewerOpen(false)}
            onNavigate={setViewerIndex}
          />
        )}

        {/* Message Action Menu (reactions + delete) */}
        {showActionMenu && (
          <MessageActionMenu
            onEmojiSelect={handleEmojiSelect}
            onDelete={handleDeleteClick}
            onEdit={handleEditClick}
            showEdit={canEdit}
            onReply={onReply ? () => onReply(message) : null}
            onCopy={message.text ? () => {
              const ta = document.createElement('textarea');
              ta.value = stripHtml(message.text);
              ta.style.cssText = 'position:fixed;opacity:0';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
            } : null}
            onClose={() => setShowActionMenu(false)}
            position="above"
            currentUserReactions={currentUserReactions}
            isOwnMessage={isOwnMessage}
            anchorRef={bubbleContainerRef}
            anchorPoint={menuAnchorPoint}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <DeleteMessageDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            onConfirm={handleConfirmDelete}
            isDeleting={isDeleting}
            isOwnMessage={isOwnMessage}
          />
        )}

        {/* Reactions display */}
        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <div className={`flex flex-wrap gap-1.5 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <TooltipProvider delayDuration={200}>
              {Object.entries(message.reactions).map(([emoji, users]) => {
                if (!users || users.length === 0) return null;
                
                const isUserReaction = currentUserId && users.includes(currentUserId);
                const names = users.map(uid => {
                  if (uid === currentUserId) return 'You';
                  const detail = participantDetails[uid] || removedParticipants[uid];
                  return detail?.name || 'User';
                });
                
                return (
                  <Tooltip key={emoji}>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={() => handleReactionClick(emoji)}
                        onTouchEnd={(e) => handleReactionTouch(emoji, e)}
                        data-ui="rx-chip"
                        data-mine={isUserReaction ? 'true' : undefined}
                        className={`
                          inline-flex items-center justify-center
                          min-w-[36px] min-h-[28px] px-2 py-1
                          rounded-full text-sm
                          shadow-sm cursor-pointer
                          transition-all duration-150
                          hover:scale-110 active:scale-95 active:bg-slate-100
                          ${isUserReaction 
                            ? 'bg-blue-100 dark:bg-blue-900/50 border-2 border-blue-400' 
                            : 'bg-card border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5'
                          }
                        `}
                        style={{ touchAction: 'manipulation' }}
                      >
                        {emoji}
                        {users.length > 1 && (
                          <span className={`ml-0.5 text-xs ${isUserReaction ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                            {users.length}
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>{names.join(', ')}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        )}
        
        {/* Status indicator for own messages - show below for last in group */}
        {isOwnMessage && isLastInGroup && (
          <div className="flex justify-end mr-1 mt-0.5">
            {isGroupChat && message.status !== 'sending' && message.status !== 'failed' ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center hover:opacity-80 transition-opacity" data-testid={`read-by-popover-${message.id}`}>
                    {getStatusIcon()}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-0" align="end" side="top">
                  <div className="max-h-60 overflow-y-auto">
                    {(() => {
                      const readBy = message.readBy || [];
                      const otherParticipants = conversationParticipants.filter(id => id !== message.senderId);
                      const seen = otherParticipants.filter(id => readBy.includes(id));
                      const notSeen = otherParticipants.filter(id => !readBy.includes(id));
                      return (
                        <>
                          {seen.length > 0 && (
                            <div>
                              <div data-ui="readby-h" className="px-3 py-1.5 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 rounded-t-xl">
                                <span data-ui="eyebrow" className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Seen ({seen.length})</span>
                              </div>
                              {seen.map(id => (
                                <div key={id} className="flex items-center gap-2 px-3 py-1">
                                  <div data-ui="readby-dot" data-seen="true" className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{participantDetails[id]?.name || removedParticipants[id]?.name || 'Unknown'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {notSeen.length > 0 && (
                            <div>
                              <div data-ui="readby-h" className={`px-3 py-1.5 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 ${seen.length > 0 ? 'border-t' : 'rounded-t-xl'}`}>
                                <span data-ui="eyebrow" className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Not yet seen ({notSeen.length})</span>
                              </div>
                              {notSeen.map(id => (
                                <div key={id} className="flex items-center gap-2 px-3 py-1">
                                  <div data-ui="readby-dot" className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                                  <span className="text-xs text-slate-400 dark:text-slate-500 truncate">{participantDetails[id]?.name || removedParticipants[id]?.name || 'Unknown'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <span className="flex items-center">
                {getStatusIcon()}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Timestamp on RIGHT side for other's messages (left-aligned bubbles) */}
      {!isOwnMessage && (
        <div className={`flex items-center ml-2 transition-opacity duration-200 ${showTimestamp ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <span data-ui="msg-ts" className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
            {message.edited && <span className="italic mr-1">edited</span>}
            {formatMessageTime(message.createdAt)}
          </span>
        </div>
      )}
    </div>
  );
}
